"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { useSessionContext } from "@/components/SupabaseProvider";
import { useProfile } from "./useProfile";

export type PricingLimitVariant =
  | "control"
  | "video-count"
  | "credit-count"
  | "export-count";

// Mirrors klap-api resolvePricingLimitVariant: only profiles created on/after
// this date enter the experiment; older users always get control — unless a
// PostHog toolbar / overrideFeatureFlags override is active (local testing).
// v4 relaunch: must equal the v4 flag's signed_up_at release condition in
// PostHog (and klap-api's PRICING_LIMIT_EXPERIMENT_LAUNCH_DATE).
export const PRICING_LIMIT_EXPERIMENT_LAUNCH_DATE = new Date(
  "2026-08-21T10:30:00Z",
);

const FLAG_KEY = "pricing-limit-style-v4";

const OVERRIDE_FEATURE_FLAGS_KEY = "$override_feature_flags";
const STORED_PERSON_PROPERTIES_KEY = "$stored_person_properties";

// Shared between /pricing and the NeedPaidPlan paywall modal so the
// experiment's value-prop copy can't drift between the two surfaces.
export const getPricingLimitSubtitle = (
  variant: PricingLimitVariant,
): string => {
  switch (variant) {
    case "export-count":
      return "Unlimited AI clipping — only pay for clips you export.";
    case "credit-count":
      return "Unlimited AI clipping — only pay for the minutes you upload.";
    case "video-count":
      return "Unlimited AI clipping — only pay for the videos you upload.";
    case "control":
      return "Only pay for clips you generate.";
  }
};

const parseVariant = (flagVariant: string | boolean | undefined | null) => {
  switch (flagVariant) {
    case "video-count":
    case "credit-count":
    case "export-count":
      return flagVariant;
    case "control":
    default:
      return "control" as const;
  }
};

// Reads the pricing-limit-style experiment variant.
//
// Unlike v3, the v4 flag enforces eligibility in its own release conditions
// (person properties signed_up_at is after launch, is_guest is not true), so
// ineligible users' events never carry the $feature/pricing-limit-style-v4
// property and can't contaminate the experiment's "Paywall Dialog Showed"
// exposure. The hook feeds those properties to flag evaluation via
// setPersonPropertiesForFlags:
// - Anonymous visitors get signed_up_at = now (honest: if they sign up it
//   will be >= launch), which lets the flag match and assign a variant.
// - Identified users get their real profile values, which also overwrites any
//   stale anonymous-set value so a pre-launch user who browsed /pricing
//   logged-out loses the variant once logged in.
//
// The flag is evaluated (and the $feature_flag_called exposure event recorded)
// for two audiences:
// - Anonymous visitors (no session): evaluated against their anonymous
//   distinct_id so the marketing pricing page can show variant copy. The v4
//   flag does NOT use experience continuity (turned off 2026-09-02): it never
//   worked with posthog-js's default person_profiles=identified_only (the
//   hash-key override write fails at login) and, while on, re-rolled ~38% of
//   signups one second after login. After identify() every evaluation, here
//   and in klap-api, hashes the profile id, so in-app display and enforcement
//   agree. Accepted trade-off: a logged-out /pricing visitor (~1% of signups)
//   may see the other variant's copy after signing up.
// - Enrolled identified users: non-guest profiles created on/after the launch
//   date (mirrors klap-api's isPricingLimitExperimentEligible gate).
//
// Guests and pre-launch profiles get "control" without touching the flag, so
// existing users can't pollute the experiment's exposure. Accepted trade-off:
// a logged-out pre-launch user can see variant copy on /pricing, then get
// control after logging in (the eligibility gate wins for identified users).
// While the session or profile is still loading, nothing is evaluated, so an
// old logged-in user is never exposed during the load window.
//
// Toolbar / console overrides win over the eligibility gate so local testing
// isn't blocked by it.
export const usePricingLimitVariant = (): PricingLimitVariant => {
  const posthog = usePostHog();
  const { session, isLoading: isSessionLoading } = useSessionContext();
  const { data: profile } = useProfile();
  const [flagVariant, setFlagVariant] = useState<
    string | boolean | undefined
  >();

  const isAnonymousVisitor = !isSessionLoading && !session;
  const isEligibleProfile =
    !!profile &&
    !profile.isGuest &&
    !!profile.createdAt &&
    new Date(profile.createdAt) >= PRICING_LIMIT_EXPERIMENT_LAUNCH_DATE;
  const shouldEvaluate = isAnonymousVisitor || isEligibleProfile;

  // Feeds the person properties the v4 flag's release conditions match on
  // (signed_up_at, is_guest) into every /flags request, so evaluation never
  // depends on person-property ingestion timing. Guarded against re-sending
  // identical values because setPersonPropertiesForFlags(_, true) triggers a
  // flags reload and this hook is mounted by several components.
  useEffect(() => {
    if (!posthog || isSessionLoading) return;
    const stored = posthog.get_property?.(STORED_PERSON_PROPERTIES_KEY) as
      | Record<string, string>
      | undefined;
    if (session) {
      if (!profile?.createdAt) return;
      const properties = {
        signed_up_at: new Date(profile.createdAt).toISOString(),
        is_guest: String(!!profile.isGuest),
      };
      if (
        stored?.signed_up_at === properties.signed_up_at &&
        stored?.is_guest === properties.is_guest
      )
        return;
      posthog.setPersonPropertiesForFlags(properties, true);
    } else {
      // Anonymous visitor: signed_up_at = now is honest (signing up later
      // means signing up after launch) and lets the flag assign a variant for
      // the marketing pricing page (not carried through identify(); see the
      // header comment). Only set once per anonymous session; posthog.reset()
      // on logout clears it.
      if (stored?.signed_up_at) return;
      posthog.setPersonPropertiesForFlags(
        { signed_up_at: new Date().toISOString(), is_guest: "false" },
        true,
      );
    }
  }, [posthog, isSessionLoading, session, profile]);

  useEffect(() => {
    if (!posthog) return;
    const update = () => {
      const overrides = posthog.get_property?.(OVERRIDE_FEATURE_FLAGS_KEY) as
        | Record<string, string | boolean>
        | undefined;
      const hasToolbarOverride =
        !!overrides &&
        typeof overrides === "object" &&
        Object.prototype.hasOwnProperty.call(overrides, FLAG_KEY);
      // getFeatureFlag records the $feature_flag_called exposure event —
      // intentionally reached only for anonymous visitors, enrolled users,
      // or overrides.
      setFlagVariant(
        hasToolbarOverride || shouldEvaluate
          ? posthog.getFeatureFlag(FLAG_KEY)
          : undefined,
      );
    };
    update();
    return posthog.onFeatureFlags(update);
  }, [posthog, shouldEvaluate]);

  return parseVariant(flagVariant);
};
